import * as React from 'react';
import * as Immutable from 'immutable';
import { combineReducers } from 'redux-immutable';
import { AjaxActionEnd, AjaxActionEndType, AjaxActionStart, AjaxActionStartType } from '../../Common/_actions/AjaxAction';
import { UIDataAction, UIDataActionType } from '../_actions/UIDataActions';
import UIDataStore, { IUIDataStore, UIDataStoreFactory } from '../_stores/UIDataStore';
import DesignLabReducer from './DesignLabReducer';
import { UserDataActionType } from '../../UserData/_actions/UserDataActions';
import { UserCartActionType } from '../../UserData/_actions/UserCartActions';
import { UserAddressActionType } from '../../UserData/_actions/UserAddressActions';
import { BrandingOptionActionType } from '../../UserData/_actions/BrandingOptionActions';
import { AuthActionType } from '../../Auth/_actions/AuthActions';
import UIToastStore from '../_stores/UIToastStore';
import GoalToastStore from '../_stores/GoalToastStore';
import { v4 as uuidv4 } from 'uuid';
import { StoreActionType } from '../../UserData/_actions/Store/StoreActions';
import { AccountStoreAction } from '../_actions/Account/AccountStoreActions';
import AccountReducer from './Account/AccountReducer';
import { StoreProductActionType } from '../../UserData/_actions/Store/StoreProductActions';
import { StoreProductListingAction } from '../../UserData/_actions/Store/StoreProductListingActions';
import { StorePurchaseReportAction } from '../../UserData/_actions/Store/StorePurchaseReportActions';
import { ProductDesignActionType } from '../../UserData/_actions/ProductDesignActions';
import { CollectionActionType } from '../../UserData/_actions/CollectionActions';
import { UserImageActionType } from '../../UserData/_actions/UserImageActions';
import { UserTaxNumberActionType } from '../../UserData/_actions/UserTaxNumberActions';

const UIDataReducer = (
	store: UIDataStore = new UIDataStore(),
	action: UIDataAction
		| AccountStoreAction
		| StoreProductListingAction
		| StorePurchaseReportAction
		| AjaxActionStart<unknown>
		| AjaxActionEnd<unknown>
): UIDataStore => {
	switch (action.type) {
		case UIDataActionType.Toast:
			return store.set('toast', new UIToastStore({
				key: uuidv4(),
				new: true,
				message: action.message,
				goalData:  action.goalData ? new GoalToastStore(action.goalData) : null,
				severity: action.severity ?? null,
			}))

		case UIDataActionType.ConsumeToast:
			return store.set('toast', new UIToastStore())

		case UIDataActionType.EditLoader:
			return store.setIn(['loaders', action.loader], action.value);

		case UIDataActionType.EditValidData:
			return store.setIn(['validData', action.dataKey], action.value);

		case UIDataActionType.StartInterval:
			clearInterval(store.get('intervals').get(action.name));
			return store.setIn(['intervals', action.name], setInterval(action.call, action.milliseconds))

		case UIDataActionType.StopInterval:
			clearInterval(store.get('intervals').get(action.name));
			return store.deleteIn(['intervals', action.name]);

		case UIDataActionType.ApiError:
			store = store.set('apiErrors', store.get('apiErrors').set(action.key, action.error))
			break;

		case UIDataActionType.ResetApiErrors:
			store = store.set('apiErrors', Immutable.Map<string, Record<string, any>>())
			break;

		case UIDataActionType.EditCreateUrl:
			return store.set('createUrl', action.createUrl);

		case AjaxActionStartType:
		case AjaxActionEndType:
			let loading = action.type == AjaxActionStartType;
			if (action.url == "ajax/api/get-app-data") store = store.setIn(["loaders", "appData"], loading);
			if (action.url == "ajax/api/user/upload-file") store = store.setIn(["loaders", "images"], loading);
			if (action.url == "ajax/api/cart/delete-cart") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/save-cart-address") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/update-cart-shipping-type") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/apply-promo-code") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/remove-promo-code") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/delete-cart-item") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/remove-cart-item-label") store = store.setIn(["loaders", "carts"], loading);
			if (action.url == "ajax/api/cart/add-cart-item-label") store = store.setIn(["loaders", "carts"], loading);
			if (/ajax\/api\/user\/stores\/[a-z0-9\-]+\/products\/publish-jobs$/.test(action.url)) store = store.setIn(["loaders", "store_addProducts"], loading);
			if (/ajax\/api\/user\/stores\/[a-z0-9-]+\/preset$/.test(action.url)) store = store.setIn(["loaders", "edit_store_preset"], loading);

			switch (action.actionType) {
				case AuthActionType.SubmitSignup:
				case AuthActionType.Authenticate:
				case AuthActionType.SubmitFbLogin:
				case AuthActionType.SubmitGoogleLogin:
				case AuthActionType.SubmitAccountLink:
				case AuthActionType.ResetPasswordRequest:
				case AuthActionType.NewPassword:
				case AuthActionType.ResendValidationLink:
					store = store.set('loaders', store.get('loaders').set('auth', loading))
					break;

				case UserDataActionType.SaveContactDetails:
					store = store.set('loaders', store.get('loaders').set('carts', loading));
					store = store.set('loaders', store.get('loaders').set('userData', loading));
					break;

				case UserDataActionType.SavePassword:
				case UserDataActionType.DeleteAccount:
					store = store.set('loaders', store.get('loaders').set('userData', loading));
					break;

				case UserCartActionType.UpdateCartItem:
					store = store.set('loaders', store.get('loaders').set('carts', loading));
					break;

				case UserAddressActionType.CreateUserAddress:
				case UserAddressActionType.UpdateUserAddress:
					store = store.set('loaders', store.get('loaders').set('edit_address', loading));
					break;

				case BrandingOptionActionType.CreateBrandingOption:
				case BrandingOptionActionType.UpdateBrandingOption:
				case BrandingOptionActionType.DeleteBrandingOption:
					store = store.set('loaders', store.get('loaders').set('edit_branding_option', loading));
					break;

				case BrandingOptionActionType.FetchBrandingOptions:
					store = store.set('loaders', store.get('loaders').set('fetch_branding_options', loading));
					break;

				case UserTaxNumberActionType.GetUserTaxNumbers:
					store = store.set('loaders', store.get('loaders').set('fetch_tax_numbers', loading));
					break;

				case StoreActionType.FetchPurchaseReportData:
					store = store.set('loaders', store.get('loaders').set('fetch_purchase_report_data', loading));
					break;

				case StoreActionType.SaveStoreSettings:
				case StoreActionType.DeleteStore:
					store = store.set('loaders', store.get('loaders').set('edit_store', loading));
					break;

				case StoreActionType.FetchStoreOverview:
					store = store.set('loaders', store.get('loaders').set('fetch_store_overview', loading));
					break;

				case StoreActionType.RefreshStoreOrders:
					store = store.set('loaders', store.get('loaders').set('refresh_store_orders', loading));
					break;

				case StoreActionType.RefreshStoreProducts:
					store = store.set('loaders', store.get('loaders').set('refresh_store_products', loading));
					break;

				case StoreProductActionType.BatchDeleteStoreProducts:
				case StoreProductActionType.BatchIgnoreStoreProducts:
				case StoreProductActionType.BatchUnignoreStoreProducts:
				case StoreProductActionType.BatchUnsyncStoreProducts:
					store = store.set('loaders', store.get('loaders').set('edit_store_products', loading));
					break;

				case ProductDesignActionType.BatchDeleteProductDesigns:
				case CollectionActionType.BatchAddToCollection:
				case CollectionActionType.BatchRemoveFromCollection:
					store = store.set('loaders', store.get('loaders').set('edit_product_designs', loading));
					break;

				case UserImageActionType.BatchDeleteImages:
					store = store.set('loaders', store.get('loaders').set('edit_images', loading));
					break;

			}
	}

	const defaultUIData = new UIDataStore
	return new UIDataStore(combineReducers<IUIDataStore, IUIDataStore>({
		toast: (store) => store ?? defaultUIData.get('toast'),
		loaders: (store) => store ?? defaultUIData.get('loaders'),
		validData: (store) => store ?? defaultUIData.get('validData'),
		apiErrors: (store) => store ?? defaultUIData.get('apiErrors'),
		scrollBlockers: (store) => store ?? defaultUIData.get('scrollBlockers'),
		intervals: (store) => store ?? defaultUIData.get('intervals'),
		designLab: DesignLabReducer,
		account: AccountReducer,
		createUrl: (store) => store ?? defaultUIData.get('createUrl'),
		recentlyViewedProductIds: (store) => store ?? defaultUIData.get('recentlyViewedProductIds'),
	}, UIDataStoreFactory)(store, action));

}

export default UIDataReducer
